import { Link, useLocation } from 'react-router-dom'
import { useAccounts } from '../store/accounts'

/**
 * Sticky pill above ExploreBar shown when the active account is
 * marked deactivated. Two distinct cases share the same banner:
 *
 *  - SessionExpired (our own code) — the refresh JWT itself expired.
 *    Solution is just re-login; the account itself is fine. Render
 *    amber with a primary "log in again" CTA.
 *  - Anything else from the PDS (AccountDeactivated / Takedown /
 *    Suspended / etc) — account is genuinely dead. Render red with
 *    a "try again" button in case it was transient.
 *
 * In both cases live polls bail and cached views render.
 */
export function DeactivatedBanner() {
  const acc = useAccounts((s) => s.accounts.find((a) => a.id === s.activeId))
  const markReactivated = useAccounts((s) => s.markReactivated)
  const loc = useLocation()

  if (!acc?.deactivated) return null

  // This banner is about YOUR active account. It's mounted globally, but it
  // shouldn't pop over someone ELSE's profile — there it reads like it's
  // describing that profile. The profile page surfaces its own per-target
  // notice instead. Still show it on your own profile (that's relevant).
  const viewing = loc.pathname.match(/^\/profile\/(.+)$/)
  if (viewing) {
    const viewedHandle = decodeURIComponent(viewing[1]).replace(/^@/, '').toLowerCase()
    const myHandle = acc.handle.replace(/^@/, '').toLowerCase()
    if (viewedHandle !== myHandle) return null
  }

  const isSessionExpired = acc.deactivationReason === 'SessionExpired'
  const reasonLabel = friendly(acc.deactivationReason)
  const since = acc.deactivatedAt
    ? new Date(acc.deactivatedAt).toLocaleString()
    : null

  const palette = isSessionExpired
    ? 'border-amber-400/30 bg-amber-500/10 text-amber-100'
    : 'border-rose-400/30 bg-rose-500/10 text-rose-100'
  const chipPalette = isSessionExpired
    ? 'border-amber-400/40 bg-amber-500/20'
    : 'border-rose-400/40 bg-rose-500/20'

  return (
    <div className={`border-b px-3 py-2 text-xs ${palette}`}>
      <div className="mx-auto flex max-w-5xl flex-wrap items-center gap-2">
        <span className="text-base">⚠️</span>
        <span
          className={`rounded-full border px-2 py-0.5 font-bold uppercase tracking-widest text-[10px] ${chipPalette}`}
        >
          {isSessionExpired ? 'session expired' : 'deactivated'}
        </span>
        <span className="flex-1">
          <strong>@{acc.handle}</strong> — {reasonLabel}. Live polls + composer
          are paused for this account; cached views still render.
          {since ? <span className="ml-1 opacity-70">noticed {since}.</span> : null}
          {/* Show the full DID: a deactivated account's handle is often
           *  stale, so the DID is the only reliable way to tell which
           *  account this banner is about. */}
          <code
            className="ml-1 select-all rounded bg-black/20 px-1 py-0.5 font-mono text-[10px] opacity-80"
            title="stable account ID — click to select (handles can change, this can't)"
          >
            {acc.did}
          </code>
        </span>
        {isSessionExpired ? (
          <Link
            to="/login"
            className="rounded-full border border-emerald-300 bg-emerald-500/30 px-3 py-0.5 text-[11px] font-bold text-white hover:bg-emerald-500/50"
            title="re-authenticate this account — bsky's refresh tokens expire every couple weeks"
          >
            log in again
          </Link>
        ) : null}
        <button
          type="button"
          onClick={() => markReactivated(acc.id)}
          className="rounded-full border border-white/20 bg-white/10 px-2 py-0.5 text-[11px] text-white hover:bg-white/20"
          title="clear the flag and let the next call try again — useful if it was a transient error"
        >
          try again
        </button>
      </div>
    </div>
  )
}

function friendly(code?: string): string {
  switch (code) {
    case 'SessionExpired':
      return 'session tokens expired — log in again to get fresh ones'
    case 'AccountDeactivated':
      return 'PDS reports the account is deactivated'
    case 'AccountTakedown':
      return 'PDS reports an admin takedown'
    case 'AccountSuspended':
      return 'PDS reports the account is suspended'
    case 'AccountInvalid':
    case 'AccountNotFound':
      return "PDS doesn't recognise this account anymore"
    case 'InvalidToken':
      return 'tokens were rejected by the PDS'
    default:
      return code ? `PDS error: ${code}` : 'PDS rejected our credentials'
  }
}
